import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { Shield, Save, Check, Eye, EyeOff, RotateCcw, Crown, Lock, Info, FileText, AlertTriangle } from 'lucide-react';
import BrandLoader from '../../../components/common/BrandLoader';
import { legalApi } from '../../../api/legalApi';
import { useError } from '../../../context/ErrorContext';
import { isPro as checkIsPro } from '../../../utils/planPermissions';
import Toggle from '../../../components/ui/Toggle';
import ConfirmDialog from '../../../components/common/ConfirmDialog';

// ── Textos default pre-redactados ──
const DEFAULT_PRIVACY_TEXT = `Este negocio recopila tu número de teléfono, nombre y datos opcionales (como fecha de cumpleaños y correo electrónico) con el único propósito de gestionar tu participación en nuestro programa de lealtad.

Tu información será utilizada exclusivamente para:
• Identificarte como miembro del programa
• Registrar tus visitas y acumulación de puntos/saldo
• Enviarte comunicaciones relacionadas con el programa (si aplica)

No compartimos, vendemos ni transferimos tu información personal a terceros. Tus datos se almacenan de forma segura y puedes solicitar su eliminación en cualquier momento contactando directamente al establecimiento.`;

const DEFAULT_RULES_TEXT = `Al participar en este programa de lealtad, aceptas las siguientes condiciones:

1. ACUMULACIÓN: Los puntos/saldo se acumulan únicamente a través de visitas o compras legítimas realizadas en el establecimiento.

2. MODIFICACIONES: El negocio se reserva el derecho de modificar, suspender o cancelar la dinámica de puntos, las recompensas disponibles y las condiciones del programa en cualquier momento y sin previo aviso.

3. USO INDEBIDO: Cualquier intento de manipulación, fraude o abuso del sistema (incluyendo, pero no limitado a: registros múltiples, uso de números ajenos, o aprovechamiento de errores del sistema) podrá resultar en la cancelación inmediata de la cuenta y la pérdida de todos los puntos/saldo acumulados.

4. PUNTOS/SALDO: Los puntos o saldo acumulados no tienen valor monetario, no son transferibles y no pueden ser canjeados por dinero en efectivo.

5. CUENTA: El negocio se reserva el derecho de cerrar o suspender cualquier cuenta que considere que viola estos términos.

6. VIGENCIA: Los puntos/saldo pueden tener fecha de expiración según las políticas vigentes del establecimiento.`;

// F1 reorden 24-oct-2026: deja de ser pantalla propia del menú. Vive como sección dentro de
// Mi Negocio › Kiosco (es lo que acepta el cliente al registrarse). La ruta vieja /dashboard/legal redirige allí.
export const LegalSettingsSection: React.FC = () => {
  const { currentUser } = useAuth();
  const { clearErrors, addError } = useError();
  const hasProAccess = checkIsPro(currentUser?.subscription);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Settings state
  const [disclaimerEnabled, setDisclaimerEnabled] = useState(false);
  const [privacyText, setPrivacyText] = useState(DEFAULT_PRIVACY_TEXT);
  const [rulesText, setRulesText] = useState(DEFAULT_RULES_TEXT);
  const [currentVersion, setCurrentVersion] = useState(1);
  const [originalPrivacyText, setOriginalPrivacyText] = useState(DEFAULT_PRIVACY_TEXT);
  const [originalRulesText, setOriginalRulesText] = useState(DEFAULT_RULES_TEXT);
  const [hasExistingConfig, setHasExistingConfig] = useState(false);

  // Track if texts have changed (to bump version)
  const textsChanged = privacyText !== originalPrivacyText || rulesText !== originalRulesText;

  const fetchSettings = useCallback(async () => {
    if (!currentUser?.businessId) return;
    try {
      setLoading(true);
      if (clearErrors) clearErrors();

      const data = await legalApi.get();

      if (data) {
        setDisclaimerEnabled(data.disclaimerEnabled || false);
        setPrivacyText(data.disclaimerPrivacyText || DEFAULT_PRIVACY_TEXT);
        setRulesText(data.disclaimerRulesText || DEFAULT_RULES_TEXT);
        setCurrentVersion(data.disclaimerVersion || 1);
        setOriginalPrivacyText(data.disclaimerPrivacyText || DEFAULT_PRIVACY_TEXT);
        setOriginalRulesText(data.disclaimerRulesText || DEFAULT_RULES_TEXT);
        setHasExistingConfig(true);
      }
    } catch (error) {
      console.error('Error al cargar configuración legal:', error);
      addError('No se pudo cargar la configuración legal. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.businessId, clearErrors, addError]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    if (!currentUser?.businessId) return;

    try {
      setSaving(true);
      if (clearErrors) clearErrors();
      setSuccess(false);

      // Si los textos cambiaron y ya había config existente, incrementar versión
      const newVersion = (textsChanged && hasExistingConfig) ? currentVersion + 1 : currentVersion;

      await legalApi.save({
        disclaimerEnabled,
        disclaimerPrivacyText: privacyText,
        disclaimerRulesText: rulesText,
        disclaimerVersion: newVersion,
      });

      setCurrentVersion(newVersion);
      setOriginalPrivacyText(privacyText);
      setOriginalRulesText(rulesText);
      setHasExistingConfig(true);

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error al guardar configuración legal:', error);
      addError('No se pudo guardar la configuración legal. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  // Restaurar PISA los textos que el negocio haya escrito: se pregunta con el diálogo del
  // manager (antes: confirm nativo del navegador, fuera de diseño).
  const [askRestore, setAskRestore] = useState(false);
  const handleRestoreDefaults = () => setAskRestore(true);
  const doRestoreDefaults = () => {
    setAskRestore(false);
    setPrivacyText(DEFAULT_PRIVACY_TEXT);
    setRulesText(DEFAULT_RULES_TEXT);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10" data-testid="legal-cargando">
        <BrandLoader size={36} />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6 w-full" data-testid="legal-seccion">
        {/* Cabecera de sección (antes era el h1 de la pantalla «Términos Legales») */}
        <div>
          <h3 className="text-lg font-medium text-content flex items-center">
            <Shield className="h-5 w-5 mr-2 text-accent" />
            Términos que acepta el cliente al registrarse
          </h3>
          <p className="mt-1 text-sm text-content-3">
            Aviso de privacidad y reglas del programa. Se guardan con su propio botón, aparte de la barra de abajo.
          </p>
        </div>

        {/* Success message */}
        {success && (
          <div className="bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/30 rounded-lg p-4 animate-fade-in-up">
            <div className="flex">
              <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-sm text-green-700 dark:text-green-300">
                  Los cambios se guardaron correctamente.
                  {textsChanged && hasExistingConfig && (
                    <span className="font-medium"> La versión se actualizó — los clientes existentes deberán re-aceptar los nuevos términos en su próxima visita.</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Toggle principal */}
        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <div className="px-4 py-5 sm:px-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-content">Habilitar Disclaimers</h3>
                <p className="mt-1 text-sm text-content-3">
                  Cuando está activo, los clientes deben aceptar tus términos para participar en el programa.
                </p>
              </div>
              <Toggle
                checked={disclaimerEnabled}
                onChange={setDisclaimerEnabled}
                label="Habilitar disclaimers"
              />
            </div>
          </div>
        </div>

        {/* Info panel cuando está habilitado */}
        {disclaimerEnabled && (
          <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-lg p-4 animate-fade-in-up">
            <div className="flex">
              <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="ml-3 text-sm text-blue-700 dark:text-blue-300">
                <p className="font-medium mb-1">¿Cómo funciona?</p>
                <p>
                  Los clientes verán estos términos al registrarse por primera vez en el kiosco. Si modificas los textos, 
                  los clientes que ya aceptaron una versión anterior deberán re-aceptar la próxima vez que hagan check-in.
                </p>
                {hasExistingConfig && (
                  <p className="mt-1 text-xs text-accent">
                    Versión actual de los términos: <span className="font-bold">v{currentVersion}</span>
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Textos del disclaimer */}
        {disclaimerEnabled && (
          <div className="space-y-6">
            {/* Aviso de Privacidad */}
            <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
              <div className="px-4 py-5 sm:px-6 border-b border-line">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-medium text-content flex items-center">
                    <Lock className="h-4 w-4 mr-2 text-content-3" />
                    Aviso de Privacidad
                  </h3>
                  {!hasProAccess && (
                    <span className="inline-flex items-center text-xs text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-500/20 px-2 py-1 rounded-full">
                      <Crown className="h-3 w-3 mr-1" /> Editable con PRO
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-content-3">
                  Describe cómo se manejan los datos personales de tus clientes.
                </p>
              </div>
              <div className="px-4 py-4 sm:px-6">
                <textarea
                  value={privacyText}
                  onChange={(e) => setPrivacyText(e.target.value)}
                  disabled={!hasProAccess}
                  rows={8}
                  className={`block w-full rounded-md border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm leading-relaxed ${
                    !hasProAccess ? 'bg-surface-2 cursor-not-allowed text-content-3' : ''
                  }`}
                  placeholder="Escribe tu aviso de privacidad aquí..."
                />
                {!hasProAccess && (
                  <p className="mt-2 text-xs text-content-3 flex items-center">
                    <Crown className="h-3 w-3 mr-1 text-yellow-500" />
                    Actualiza a PRO para personalizar este texto. El texto predeterminado se mostrará a tus clientes.
                  </p>
                )}
              </div>
            </div>

            {/* Reglas del Programa */}
            <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
              <div className="px-4 py-5 sm:px-6 border-b border-line">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-medium text-content flex items-center">
                    <FileText className="h-4 w-4 mr-2 text-content-3" />
                    Reglas del Programa
                  </h3>
                  {!hasProAccess && (
                    <span className="inline-flex items-center text-xs text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-500/20 px-2 py-1 rounded-full">
                      <Crown className="h-3 w-3 mr-1" /> Editable con PRO
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-content-3">
                  Incluye las condiciones de uso, reservas de derechos y políticas de fraude.
                </p>
              </div>
              <div className="px-4 py-4 sm:px-6">
                <textarea
                  value={rulesText}
                  onChange={(e) => setRulesText(e.target.value)}
                  disabled={!hasProAccess}
                  rows={10}
                  className={`block w-full rounded-md border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm leading-relaxed ${
                    !hasProAccess ? 'bg-surface-2 cursor-not-allowed text-content-3' : ''
                  }`}
                  placeholder="Escribe las reglas de tu programa aquí..."
                />
                {!hasProAccess && (
                  <p className="mt-2 text-xs text-content-3 flex items-center">
                    <Crown className="h-3 w-3 mr-1 text-yellow-500" />
                    Actualiza a PRO para personalizar este texto. El texto predeterminado se mostrará a tus clientes.
                  </p>
                )}
              </div>
            </div>

            {/* Version bump warning */}
            {textsChanged && hasExistingConfig && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 animate-fade-in-up">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="ml-3 text-sm text-amber-700">
                    <p className="font-medium">Los textos han sido modificados</p>
                    <p className="mt-1">
                      Al guardar, la versión se incrementará de v{currentVersion} a v{currentVersion + 1}. 
                      Todos los clientes que ya aceptaron deberán re-aceptar los nuevos términos en su próxima visita al kiosco.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Previsualización */}
            <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
              <div className="px-4 py-4 sm:px-6">
                <button
                  type="button"
                  onClick={() => setShowPreview(!showPreview)}
                  className="inline-flex items-center text-sm font-medium text-accent hover:text-blue-800 dark:text-blue-300 transition-colors"
                >
                  {showPreview ? (
                    <><EyeOff className="h-4 w-4 mr-2" /> Ocultar previsualización</>
                  ) : (
                    <><Eye className="h-4 w-4 mr-2" /> Ver cómo lo verá el cliente</>
                  )}
                </button>
              </div>

              {showPreview && (
                <div className="px-4 pb-6 sm:px-6">
                  <div className="border-2 border-dashed border-line rounded-xl p-6 bg-surface-2">
                    <div className="max-w-sm mx-auto">
                      {/* Simulated dialog */}
                      <div className="bg-surface rounded-2xl shadow-lg p-6">
                        <div className="flex flex-col items-center mb-4">
                          <div className="bg-blue-100 dark:bg-blue-500/20 p-3 rounded-full mb-2">
                            <Shield className="w-6 h-6 text-accent" />
                          </div>
                          <h3 className="text-lg font-bold text-content">Términos y Condiciones</h3>
                          <p className="text-content-3 text-xs mt-1">Por favor revisa y acepta para continuar</p>
                        </div>

                        <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
                          <div className="border border-line rounded-lg p-3">
                            <p className="text-xs font-semibold text-content-2 mb-1">🔒 Aviso de Privacidad</p>
                            <p className="text-xs text-content-3 line-clamp-3">{privacyText.substring(0, 150)}...</p>
                          </div>
                          <div className="border border-line rounded-lg p-3">
                            <p className="text-xs font-semibold text-content-2 mb-1">📋 Reglas del Programa</p>
                            <p className="text-xs text-content-3 line-clamp-3">{rulesText.substring(0, 150)}...</p>
                          </div>
                        </div>

                        <div className="flex items-start gap-2 mb-3 border-t pt-3">
                          <div className="w-4 h-4 border-2 border-line rounded mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-content-2">He leído y acepto los términos y condiciones...</p>
                        </div>

                        <div className="flex gap-2">
                          <div className="flex-1 bg-canvas text-content-3 py-2 rounded-lg text-xs text-center font-medium">Cancelar</div>
                          <div className="flex-1 bg-blue-400 text-white py-2 rounded-lg text-xs text-center font-medium opacity-50">Aceptar</div>
                        </div>
                      </div>

                      <p className="text-xs text-content-3 text-center mt-3 italic">
                        Vista previa — así se verá en el kiosco
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center justify-between bg-surface shadow rounded-lg px-4 py-4 sm:px-6 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <div className="flex items-center space-x-3">
            {disclaimerEnabled && hasProAccess && (
              <button
                type="button"
                onClick={handleRestoreDefaults}
                className="inline-flex items-center px-3 py-2 border border-line shadow-sm text-sm font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Restaurar textos
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Guardar términos
              </>
            )}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={askRestore}
        title="Restaurar textos predeterminados"
        icon={<RotateCcw className="h-5 w-5 text-amber-500 mr-2.5 flex-shrink-0" />}
        confirmLabel="Restaurar"
        cancelLabel="Conservar los míos"
        message={
          <>
            <p>Se reemplazan tu aviso de privacidad y tus reglas por los textos que trae Lealify.</p>
            <p className="mt-2 text-xs text-content-3">Lo que hayas escrito se pierde al guardar.</p>
          </>
        }
        onCancel={() => setAskRestore(false)}
        onConfirm={doRestoreDefaults}
      />
    </>
  );
};

// Ruta vieja: /dashboard/legal → Mi Negocio › Kiosco.
const LegalSettingsPage: React.FC = () => <Navigate to="/dashboard/business?tab=kiosk" replace />;

export default LegalSettingsPage;